import type { PermissionCode } from '../permissions';

/**
 * Who triggers a transition. Some transitions are reserved to trusted server-side actors
 * (e.g. a payment can only become `succeeded` from a verified Stripe webhook).
 */
export type ActorKind = 'user' | 'system' | 'stripe_webhook';

export interface TransitionDef<S extends string> {
  from: readonly S[];
  to: S;
  /** permission required when the actor is a user (null = only non-user actors) */
  permission: PermissionCode | null;
  /** reason category (administrable `reasons` referential) required for this transition */
  reasonCategory?: string;
  requiresComment?: boolean;
  /** allowed actor kinds; defaults to ['user', 'system'] */
  actors?: readonly ActorKind[];
}

export interface StateMachine<S extends string> {
  name: string;
  states: readonly S[];
  initial: S;
  /** other states a record may be created in (e.g. a dossier captured as draft) */
  entryStates?: readonly S[];
  terminal: readonly S[];
  labels: Record<S, string>;
  transitions: readonly TransitionDef<S>[];
}

export function findTransition<S extends string>(
  machine: StateMachine<S>,
  from: S,
  to: S,
  actor: ActorKind,
): TransitionDef<S> | undefined {
  return machine.transitions.find(
    (t) => t.to === to && t.from.includes(from) && (t.actors ?? ['user', 'system']).includes(actor),
  );
}

export function availableTransitions<S extends string>(
  machine: StateMachine<S>,
  from: S,
  actor: ActorKind,
): TransitionDef<S>[] {
  return machine.transitions.filter(
    (t) => t.from.includes(from) && (t.actors ?? ['user', 'system']).includes(actor),
  );
}

/** Static sanity checks, used by unit tests on every machine. */
export function validateMachine<S extends string>(machine: StateMachine<S>): string[] {
  const errors: string[] = [];
  const known = new Set<string>(machine.states);
  if (!known.has(machine.initial)) errors.push(`initial state ${machine.initial} unknown`);
  for (const t of machine.transitions) {
    if (!known.has(t.to)) errors.push(`unknown target ${t.to}`);
    for (const f of t.from) {
      if (!known.has(f)) errors.push(`unknown source ${f}`);
      if (f === t.to) errors.push(`self transition ${f}`);
    }
  }
  for (const s of machine.states) {
    if (!machine.labels[s]) errors.push(`missing label for ${s}`);
    const reachable =
      s === machine.initial || machine.entryStates?.includes(s) || machine.transitions.some((t) => t.to === s);
    if (!reachable) errors.push(`state ${s} unreachable`);
    const hasExit = machine.transitions.some((t) => t.from.includes(s));
    if (!hasExit && !machine.terminal.includes(s)) errors.push(`state ${s} is a dead end`);
  }
  return errors;
}
